import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShoppingBag, Image as ImgIcon } from "lucide-react";
import { shopApi } from "../../lib/shop-api";

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

export const ShopPreview = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    shopApi
      .listProducts({ sort: "newest", limit: 8 })
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && products.length === 0) return null;

  return (
    <section
      id="tienda"
      data-testid="shop-preview-section"
      className="relative bg-[#0A0908] py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, #C89B3C 0%, transparent 40%), radial-gradient(circle at 80% 80%, #8B4513 0%, transparent 45%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
        >
          <div>
            <div className="flex items-center gap-3 text-[#C89B3C] text-xs tracking-[0.4em] uppercase font-semibold mb-4">
              <span className="h-px w-10 bg-[#C89B3C]" />
              Tienda Online
            </div>
            <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-[#F5F0E6] leading-[0.95]">
              Compra en <em className="text-[#C89B3C] not-italic font-light italic">línea</em>
              <br />
              recoge en Hollister.
            </h2>
          </div>
          <Link
            to="/shop"
            data-testid="shop-preview-view-all"
            className="group inline-flex items-center gap-3 self-start md:self-end px-6 py-3 border border-[#C89B3C]/40 text-[#C89B3C] hover:bg-[#C89B3C] hover:text-[#0A0908] font-semibold rounded-full transition-all"
          >
            Ver toda la tienda
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="aspect-[3/4] bg-[#1A1612] border border-white/5 rounded-xl animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {products.slice(0, 8).map((p, idx) => {
              const img = (p.images || [])[0];
              const out = (p.total_stock || 0) === 0;
              const onSale =
                p.on_sale && p.compare_at_price && p.compare_at_price > p.price;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: idx * 0.06 }}
                >
                  <Link
                    to={`/shop/product/${p.slug}`}
                    data-testid={`shop-preview-card-${p.slug}`}
                    className="group block"
                  >
                    <div className="relative aspect-[3/4] bg-[#1A1612] border border-white/5 group-hover:border-[#C89B3C]/40 rounded-xl overflow-hidden transition-all">
                      {img ? (
                        <img
                          src={img}
                          alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#C89B3C]/20">
                          <ImgIcon size={56} />
                        </div>
                      )}
                      {/* badges */}
                      <div className="absolute top-3 left-3 flex flex-col gap-2">
                        {p.new_arrival && (
                          <span className="px-2 py-0.5 bg-[#C89B3C] text-[#0A0908] text-[10px] font-black tracking-widest uppercase rounded-full">
                            Nuevo
                          </span>
                        )}
                        {onSale && (
                          <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-black tracking-widest uppercase rounded-full">
                            Oferta
                          </span>
                        )}
                      </div>
                      {out && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="px-3 py-1 bg-[#F5F0E6] text-[#0A0908] text-xs font-bold tracking-widest uppercase rounded-full">
                            Agotado
                          </span>
                        </div>
                      )}
                      {/* hover CTA */}
                      <div className="absolute inset-x-3 bottom-3 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                        <div className="flex items-center justify-center gap-2 bg-[#F5F0E6] text-[#0A0908] text-xs font-bold tracking-wider uppercase py-2 rounded-full">
                          <ShoppingBag size={14} /> Ver producto
                        </div>
                      </div>
                    </div>
                    <div className="pt-3 px-1">
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#C89B3C]/70 mb-1">
                        {p.category}
                      </p>
                      <h3 className="font-['Playfair_Display'] text-lg text-[#F5F0E6] leading-tight line-clamp-2 group-hover:text-[#C89B3C] transition-colors">
                        {p.name}
                      </h3>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-[#F5F0E6] font-semibold">
                          {money(p.price)}
                        </span>
                        {onSale && (
                          <span className="text-xs text-[#F5F0E6]/40 line-through">
                            {money(p.compare_at_price)}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
